import React from 'react';
import {
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  Box,
  Typography,
  Checkbox
} from '@mui/material';
import {
  StyledTableHeadCell,
  TableWrapper,
  StyledTable,
  checkboxSx
} from './DataTable.styles';

export interface WithEmptyStateProps {
  emptyTitle?: string;
  emptyDescription?: string;
}

export function withEmptyState<P extends { data: any[]; columns: any[]; pagination?: boolean }>(
  Component: React.ComponentType<P>
) {
  return function EnhancedComponent(props: P & WithEmptyStateProps) {
    const {
      data,
      columns,
      emptyDescription,
      emptyTitle = 'Data not available',
    } = props;

    // Filter out empty state specific props to keep the underlying component clean
    const componentProps = { ...props };
    delete componentProps.emptyTitle;
    delete componentProps.emptyDescription;

    if (data && data.length > 0) {
      return <Component {...(componentProps as unknown as P)} />;
    }

    return (
      <TableWrapper>
        <TableContainer
          component={Paper}
          square
          style={{ boxShadow: 'none', border: 'none' }}
        >
          <StyledTable>
            <TableHead>
              <TableRow>
                {columns.map((column) => {
                  if (column.type === 'checkbox') {
                    return (
                      <StyledTableHeadCell
                        key={column.id}
                        $width={column.width || 80}
                      >
                        <Checkbox
                          sx={checkboxSx}
                          disabled
                        />
                      </StyledTableHeadCell>
                    );
                  }
                  return (
                    <StyledTableHeadCell key={column.id} $width={column.width}>
                      {column.label && <Typography>{column.label}</Typography>}
                    </StyledTableHeadCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 10,
                      px: 3
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter',
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#1D2939',
                        lineHeight: '24px',
                        mb: 0.5
                      }}
                    >
                      {emptyTitle}
                    </Typography>
                    {emptyDescription && (
                      <Typography
                        sx={{
                          fontFamily: 'Inter',
                          fontSize: '13px',
                          fontWeight: 400,
                          color: '#667085',
                          lineHeight: '20px',
                          maxWidth: '350px',
                          textAlign: 'center'
                        }}
                      >
                        {emptyDescription}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            </TableBody>
          </StyledTable>
        </TableContainer>
      </TableWrapper>
    );
  };
}
